import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Search, Menu, X, Sun, Moon } from "lucide-react";
import { slugify } from "../lib/utils-app";
import { useTheme } from "../contexts/ThemeContext";

const NAV_GROUPS = [
  { label: "Global Arbitration", slug: "global-arbitration-news" },
  { label: "India Arbitration",  slug: "india-arbitration-odr" },
  { label: "ODR",                slug: "digital-hearings" },
  { label: "Mediation",          slug: "arbitration-explainers" },
  { label: "ICC",                slug: "icc-arbitration" },
  { label: "ICSID",              slug: "icsid-arbitration" },
  { label: "UNCITRAL",           slug: "uncitral-updates" },
  { label: "Enforcement",        slug: "award-enforcement" },
  { label: "Institutions",       slug: "__institutions__" },
  { label: "Case Briefs",        slug: "set-aside-and-annulment" },
];

const Logo = () => (
  <Link to="/" data-testid="site-logo-link" className="flex items-center gap-2.5">
    <span className="flex items-center justify-center w-8 h-8 rounded-md border border-[var(--io-gold)]/40 bg-gradient-to-br from-[var(--io-gold)]/15 to-transparent">
      <span className="font-serif text-[16px] text-[var(--io-gold)] leading-none">iO</span>
    </span>
    <span className="leading-none">
      <span className="font-serif text-[20px] md:text-[22px] text-[var(--io-text)] tracking-tight">India<span className="text-[var(--io-gold)]">ODR</span></span>
      <span className="hidden md:block kicker mt-0.5">Arbitration & ODR Intelligence</span>
    </span>
  </Link>
);

const navTo = (g) => g.slug === "__institutions__" ? "/institutions" : `/category/${g.slug}`;

export default function Header() {
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();

  return (
    <header className="sticky top-0 z-40 io-glass" data-testid="site-header">
      {/* Utility strip (Admin removed per spec) */}
      <div className="hairline-b border-[var(--io-border)] bg-[var(--io-bg)]/60">
        <div className="max-w-[1380px] mx-auto px-4 md:px-8 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--io-text-2)]">
            <span className="signal-dot" aria-hidden="true" />
            <span>Live intelligence · {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
          </div>
          <div className="hidden md:flex items-center gap-5 text-[11px] font-sans text-[var(--io-text-2)]">
            <Link to="/editorial-policy" className="hover:text-[var(--io-gold)]" data-testid="top-editorial-policy-link">Editorial Policy</Link>
            <Link to="/correction-policy" className="hover:text-[var(--io-gold)]" data-testid="top-correction-policy-link">Corrections</Link>
            <Link to="/about" className="hover:text-[var(--io-gold)]" data-testid="top-about-link">About</Link>
            <Link to="/contact" className="hover:text-[var(--io-gold)]" data-testid="top-contact-link">Contact</Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-[1380px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5" data-testid="desktop-nav">
          {NAV_GROUPS.slice(0, 8).map((g) => (
            <NavLink
              key={g.label}
              to={navTo(g)}
              data-testid={`nav-${slugify(g.label)}`}
              className={({ isActive }) =>
                `relative text-[13px] font-sans tracking-wide transition-colors pb-[4px] ${
                  isActive
                    ? "text-[var(--io-gold)] after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-[2px] after:bg-[var(--io-gold)]"
                    : "text-[var(--io-text)] hover:text-[var(--io-gold)]"
                }`
              }
            >
              {g.label}
            </NavLink>
          ))}
        </nav>

        {/* Right actions (Admin button removed from top per spec) */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={toggle}
            className="p-2 rounded-md border border-transparent hover:border-[var(--io-gold)]/40 text-[var(--io-text-2)] hover:text-[var(--io-gold)]"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun size={18}/> : <Moon size={18}/>}
          </button>
          <Link to="/search" className="p-2 rounded-md border border-transparent hover:border-[var(--io-gold)]/40 text-[var(--io-text-2)] hover:text-[var(--io-gold)]" data-testid="header-search-link" aria-label="Search">
            <Search size={18} />
          </Link>
          <Link to="/digest" className="hidden md:inline-flex btn btn-primary !py-1.5 !px-3 !text-[12px]" data-testid="header-subscribe-link">Subscribe</Link>
          <button className="lg:hidden p-2 text-[var(--io-text)]" onClick={() => setOpen(true)} aria-label="Menu" data-testid="mobile-menu-open">
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* Subtle gold ribbon */}
      <div className="h-[1px]" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.5), transparent)" }} />

      {/* Mobile sheet */}
      {open && (
        <div className="fixed inset-0 z-50 bg-[var(--io-bg)]/95 backdrop-blur-lg" data-testid="mobile-menu">
          <div className="max-w-[1380px] mx-auto px-6 py-6 flex items-center justify-between">
            <Logo />
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-[var(--io-text)]" data-testid="mobile-menu-close">
              <X size={24} />
            </button>
          </div>
          <div className="px-6 pt-4 pb-8 divide-y divide-[var(--io-border)]">
            {NAV_GROUPS.map((g) => (
              <NavLink
                key={g.label}
                to={navTo(g)}
                onClick={() => setOpen(false)}
                className="block py-4 font-serif text-[22px] text-[var(--io-text)] hover:text-[var(--io-gold)]"
                data-testid={`mobile-nav-${slugify(g.label)}`}
              >
                {g.label}
              </NavLink>
            ))}
            <div className="pt-6 flex gap-3">
              <Link to="/search" onClick={() => setOpen(false)} className="btn btn-ghost flex-1 justify-center" data-testid="mobile-search-link">Search</Link>
              <Link to="/digest" onClick={() => setOpen(false)} className="btn btn-primary flex-1 justify-center" data-testid="mobile-subscribe-link">Subscribe</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
